import { TaxRegistrationStatus, EntityTaxVM } from './../../../../models/entity';
import { UserFacade, ITaxStatus } from 'src/app/store/user/facade/user.facade';
import { formatIrdNumber } from './../../../../utils/format-ird';
import { TokenService } from 'src/app/services/token/token.service';
import { IAppState } from './../../../../store/state/app.state';
import { getUserState } from './../../../../store/user/selectors/user.selectors';
import { IUserState } from './../../../../store/state/user.state';
import { AccountVM } from '../../../../models/account';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { Store, select } from '@ngrx/store';
import { NgForm, UntypedFormGroup, UntypedFormControl, Validators } from '@angular/forms';
import { Component, OnInit, ViewChild, OnDestroy } from '@angular/core';
import * as UserActions from '../../../../store/user/actions/user.actions';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { UtilitiesFacades } from 'src/app/store/utilities/facades/utilities.facades';
import { AppConfig } from '../../../../app.config';
import { ILinkSettings } from '../../../../models/app-config.model';
import { InvestorFacade } from '../../../../store/investor/facade/investor.facade';


@Component({
  selector: "app-profile",
  templateUrl: "./profile.component.html",
  styleUrls: ["./profile.component.scss"]
})
export class ProfileComponent implements OnInit, OnDestroy {

  @ViewChild('f') form: NgForm;
  public formattedIrd: string;
  public irdUnavailable = false;
  userState: IUserState;
  userProfileForm: UntypedFormGroup;
  userEntityId: string;
  userSubscription: Subscription;
  taxStatusSubscription: Subscription;
  userProfileSubscription: Subscription;
  userTaxStatusSubscription: Subscription;
  isKiwiSaverSubscription: Subscription;
  userProfile: AccountVM;
  failText: string;
  successText: string;
  loading = false;
  showSpinner = false;
  profileUpdatedFail = false;
  profileUpdatedSuccess = false;
  showFormUpdatedSuccess = false;
  enableSubmitButton = false;
  phoneNumberInvalid = false;
  taxInputEnabled: boolean;
  taxStatus: ITaxStatus;
  isKiwiSaver: boolean;
  supportEmail: string;
  supportPhone: string;


  constructor(
    private store: Store<IAppState>,
    private router: Router,
    private tokenService: TokenService,
    private facade: InvestorFacade,
    private userFacade: UserFacade,
    private appConfig: AppConfig,
    private utilitiesFacade: UtilitiesFacades
  ) {
    const links: ILinkSettings = this.appConfig.getLinks();
    this.supportEmail = links.supportEmail;
    this.supportPhone = links.supportPhone;
  }


  ngOnInit() {
    this.store.dispatch(new UserActions.ResetUserProfileForm());

    this.userProfileForm = new UntypedFormGroup({
      'firstName': new UntypedFormControl({ value: '', disabled: true }, [Validators.required]),
      'middleName': new UntypedFormControl({ value: '', disabled: true }),
      'lastName': new UntypedFormControl({ value: '', disabled: true }, [Validators.required]),
      'email': new UntypedFormControl({ value: '', disabled: true }, [Validators.required, Validators.email]),
      'phone': new UntypedFormControl(null, [Validators.minLength(8), Validators.maxLength(10), Validators.pattern("^[0-9]*$")]),
      'taxNumber': new UntypedFormControl(null, [Validators.minLength(8), Validators.maxLength(11), Validators.pattern(/^\d{2,3}\-\d{3}\-\d{2,3}$/)])
    });


    this.isKiwiSaverSubscription = this.facade.isKiwiSaverInvestor$.subscribe(x => {
      this.isKiwiSaver = x;
      if (this.isKiwiSaver) {
        // read-only IRD number for KS
        this.userProfileForm.controls["taxNumber"].disable();
      }
    });


    this.userProfileSubscription = this.userFacade.userProfileWhenNotNull$.subscribe((userProfile: AccountVM) => {
      this.userProfile = userProfile;
      this.userEntityId = this.userProfile.userEntityId;
    });


    this.userSubscription = this.store.pipe(select(getUserState))
      .subscribe((state: IUserState) => {
        if (!state.viewProfile) {
          return;
        }

        this.userState = state;
        this.profileUpdatedFail = this.userState.viewProfile.profileUpdatedFail;
        this.profileUpdatedSuccess = this.userState.viewProfile.profileUpdatedSuccess;
        this.loading = this.userState.viewProfile.loading;
        this.failText = this.userState.viewProfile.failText;
        this.successText = this.userState.viewProfile.successText;
        this.showSpinner = this.loading;

        if (this.userState.viewProfile.profileUpdatedFail) {
          return;
        } else {
          this.setProfileValues();
        }

        this.showFormUpdatedSuccess = (this.profileUpdatedSuccess && !this.loading);

      });

    this.taxStatusSubscription = this.userFacade.taxStatus$.subscribe((taxStatus: ITaxStatus) => {
      this.taxStatus = taxStatus;
      if (!!this.taxStatus) {
        this.taxInputEnabled = (this.taxStatus.taxRegistrationStatus !== TaxRegistrationStatus.NOT_APPLICABLE) ? true : false;
        if (this.taxInputEnabled) {
          this.irdUnavailable = (this.taxStatus.taxNumber === '000-000-000');
          this.updateTaxForm();
        }
      }
    });
  }

  onInputChange(form: NgForm) {
    this.enableSubmitButton = this.haveValuesChanged(form);
  }

  haveValuesChanged(form: NgForm): boolean {
    const phone = form.value.phone;
    let formDirty = false;
    for (const key in form.value) {
      if (key) {
        if (key === "phone") {
          if (phone !== this.userProfile.phone) {
            formDirty = true;
            if (
              this.userProfileForm.controls.phone.errors &&
              this.userProfileForm.controls.phone.errors.pattern
            ) {
              this.phoneNumberInvalid = true;
            } else {
              this.phoneNumberInvalid = false;
            }
          }
        } else if (key !== 'taxNumber') {
          if (form.value[key] !== this.userProfile[key]) {
            formDirty = true;
          }
        } else if (key === 'taxNumber' && this.taxInputEnabled) {
          if (!!this.userProfileForm.controls['taxNumber'].value && this.userProfileForm.controls['taxNumber'].value !== this.userState.viewProfile.taxStatus[key]) {
            formDirty = true;
          }
        }
      }
    }
    return formDirty;
  }

  onSubmit(form: NgForm) {

    const accountModel: AccountVM = {
      ...this.userProfile,
      phone: form.value.phone,
      userEntityId: this.userEntityId
    };

    const taxNumber = this.userProfileForm.controls['taxNumber'].value;
    const entityTaxModel: EntityTaxVM = {
      taxNumber,
      userEntityId: this.userProfile.userEntityId
    };

    this.store.dispatch(new UserActions.UpdateUserProfile(accountModel));
    this.store.dispatch(new UserActions.SetTaxStatus(entityTaxModel));
  }

  formatIrd(event: any) {
    this.formattedIrd = formatIrdNumber(event);
    this.userProfileForm.patchValue({
      'taxNumber': this.formattedIrd
    });
  }

  setProfileValues() {
    if(!!this.userProfile){
      this.userProfileForm.patchValue({
        'firstName': this.userProfile.firstName,
        'middleName': this.userProfile.middleName,
        'lastName': this.userProfile.lastName,
        'phone': this.userProfile.phone,
        'email': this.userProfile.email
      });
    }
  }


  onResetAndClose() {
    this.setProfileValues();
    if (this.taxInputEnabled) {
      this.updateTaxForm();
    }
    setTimeout(() => {
      this.router.navigate([{ outlets: { sidebar: null } }]);
    }, 300);
    this.store.dispatch(new UserActions.UserProfileEditClose());
  }

  onCheckIRDUnavailable($event: MatCheckboxChange) {
    this.irdUnavailable = $event.checked;
    this.updateTaxForm();
  }

  updateTaxForm() {

    if (this.irdUnavailable) {
      this.userProfileForm.patchValue({
        'taxNumber': '000-000-000'
      });
      this.userProfileForm.controls['taxNumber'].disable();
    } else {
      this.userProfileForm.patchValue({
        'taxNumber': this.taxStatus.taxNumber
      });
      // read-only IRD number for KS
      (this.isKiwiSaver) ? this.userProfileForm.controls["taxNumber"].disable() : this.userProfileForm.controls["taxNumber"].enable();
    }
  }

  ngOnDestroy() {
    this.userSubscription.unsubscribe();
    this.userProfileSubscription.unsubscribe();
    this.taxStatusSubscription.unsubscribe();
    this.isKiwiSaverSubscription.unsubscribe();
    this.store.dispatch(new UserActions.ResetUserProfileForm());
  }
}
